/**
 * MermaidBlock — ```mermaid fences, with the library LAZY-LOADED (#930).
 *
 * mermaid is ~a megabyte minified. The component itself is tiny and imported
 * statically; the LIBRARY arrives via `import('mermaid')` inside an effect, so
 * Rollup emits it as its own chunk that no page pays for until a mermaid fence
 * actually renders. The main bundle stays flat — that constraint is ratified,
 * not aspirational.
 *
 * Failure model (streaming-friendly): until a parse+render succeeds, the plain
 * fenced code block (`fallback`) stays on screen. While a fence is still
 * streaming in, parse fails on the partial text and the reader simply sees the
 * code — when the fence completes, the diagram replaces it. A diagram that
 * never parses stays a code block forever, which is the honest degradation.
 * Once something HAS rendered, a later invalid prefix keeps the last good SVG
 * instead of flickering back to text.
 *
 * Theming: mermaid inline-styles its SVG through khroma, which cannot parse
 * the oklch() literals our tokens resolve to — so the diagram uses mermaid's
 * built-in `neutral`/`dark` theme, chosen from the app theme at render time
 * (re-chosen on every render call). Fonts DO come from the token layer.
 */
import { useEffect, useRef, useState } from 'react';
import type { ReactNode } from 'react';

/** Debounce so every streaming chunk doesn't spin a render of a doomed prefix. */
const RENDER_DEBOUNCE_MS = 150;

let renderSeq = 0;

interface MermaidBlockProps {
  code: string;
  fallback: ReactNode;
}

export default function MermaidBlock({ code, fallback }: MermaidBlockProps) {
  const [svg, setSvg] = useState<string | null>(null);
  const idRef = useRef(`chat-mermaid-${++renderSeq}`);

  useEffect(() => {
    let cancelled = false;
    const timer = setTimeout(async () => {
      try {
        const mermaid = (await import('mermaid')).default;
        if (cancelled) return;
        mermaid.initialize({
          startOnLoad: false,
          securityLevel: 'strict',
          suppressErrorRendering: true,
          theme: document.documentElement.classList.contains('dark') ? 'dark' : 'neutral',
          fontFamily: 'var(--stevens-sans)',
        });
        await mermaid.parse(code);
        const { svg: rendered } = await mermaid.render(`${idRef.current}-${++renderSeq}`, code);
        if (!cancelled) setSvg(rendered);
      } catch {
        // Invalid (or still-streaming) diagram source: keep the current state —
        // fallback if nothing rendered yet, last good SVG otherwise.
      }
    }, RENDER_DEBOUNCE_MS);
    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
  }, [code]);

  if (!svg) return <>{fallback}</>;
  // securityLevel: 'strict' sanitizes the SVG mermaid hands back.
  return <div className="chat-mermaid" dangerouslySetInnerHTML={{ __html: svg }} />;
}
